import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from 'services'

// VRG-91 / VERA-246: persist the Tags inventory settings per tag SKU. Split into
// two mutations mirroring the two modals + their separate permissions:
//   • useInventorySettingsMutations → alert settings   (tags_inventory_settings)
//   • useAutoOrderMutations         → auto-reorder      (tags_auto_order)

// ─── Alert settings ───────────────────────────────────────────────────────────

export type UpdateInventorySettingsRequest = {
  tag_product_id: string
  threshold: number
  threshold_type: 'number' | 'percentage'
  send_threshold_email: boolean
}

export type UpdateInventorySettingsResponse = {
  success: true
  threshold: number
  threshold_type: string
  send_threshold_email: boolean
}

const updateInventorySettings = async ({
  tag_product_id,
  ...body
}: UpdateInventorySettingsRequest): Promise<UpdateInventorySettingsResponse> => {
  const response = await api.patch(
    `/shop/tags/inventory/${encodeURIComponent(tag_product_id)}/settings`,
    body,
  )
  return response.data
}

// ─── Auto-reorder ─────────────────────────────────────────────────────────────

export type UpdateAutoOrderRequest = {
  tag_product_id: string
  auto_order: boolean
  auto_order_qty?: number | null
  auto_order_payment_method_id?: string | null
}

export type UpdateAutoOrderResponse = {
  success: true
  auto_order: boolean
  auto_order_qty: number | null
  auto_order_payment_method_id: string | null
}

const updateAutoOrder = async ({
  tag_product_id,
  ...body
}: UpdateAutoOrderRequest): Promise<UpdateAutoOrderResponse> => {
  const response = await api.patch(
    `/shop/tags/inventory/${encodeURIComponent(tag_product_id)}/auto-order`,
    body,
  )
  return response.data
}

// Both surfaces write the same inventory row, so refresh the list + the detail
// page's threshold query after either save.
const invalidateInventory = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['tag-inventory'] })
  queryClient.invalidateQueries({ queryKey: ['tag-inventory-threshold'] })
}

type SettingsOptions = {
  onSuccess?: (data: UpdateInventorySettingsResponse) => void
  onError?: (error: Error) => void
}

export const useInventorySettingsMutations = (options?: SettingsOptions) => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    UpdateInventorySettingsResponse,
    Error,
    UpdateInventorySettingsRequest
  >({
    mutationFn: updateInventorySettings,
    onSuccess: (data) => {
      invalidateInventory(queryClient)
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    updateInventorySettings: mutation.mutate,
    updateInventorySettingsAsync: mutation.mutateAsync,
    isSaving: mutation.isPending,
    saveError: mutation.error,
  }
}

type AutoOrderOptions = {
  onSuccess?: (data: UpdateAutoOrderResponse) => void
  onError?: (error: Error) => void
}

export const useAutoOrderMutations = (options?: AutoOrderOptions) => {
  const queryClient = useQueryClient()

  const mutation = useMutation<UpdateAutoOrderResponse, Error, UpdateAutoOrderRequest>({
    mutationFn: updateAutoOrder,
    onSuccess: (data) => {
      invalidateInventory(queryClient)
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    updateAutoOrder: mutation.mutate,
    updateAutoOrderAsync: mutation.mutateAsync,
    isSaving: mutation.isPending,
    saveError: mutation.error,
  }
}
